"use client";

import { Box, SxProps, Theme } from "@mui/material";
import { ReactNode, useId, useRef, useState } from "react";
import {
  actionSx,
  doubleRule,
} from "@/src/components/common/broadsheet/tokens";

export type BroadsheetTabItem = {
  id: string;
  label: string;
  content: ReactNode;
};

export default function BroadsheetTabs({
  items,
  label,
  sx,
}: {
  items: BroadsheetTabItem[];
  label: string;
  sx?: SxProps<Theme>;
}) {
  const baseId = useId();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  if (items.length === 0) return null;

  const current = Math.min(active, items.length - 1);

  const focusTab = (index: number) => {
    const next = (index + items.length) % items.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const moves: Record<string, number> = {
      ArrowRight: current + 1,
      ArrowLeft: current - 1,
      Home: 0,
      End: items.length - 1,
    };

    if (event.key in moves) {
      event.preventDefault();
      focusTab(moves[event.key]);
    }
  };

  const tabId = (index: number) => `${baseId}-tab-${items[index].id}`;
  const panelId = (index: number) => `${baseId}-panel-${items[index].id}`;

  return (
    <Box sx={sx}>
      <Box
        role="tablist"
        aria-label={label}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 0.375,
          pb: 0.1875,
          ...doubleRule,
        }}
      >
        {items.map((item, index) => (
          <Box
            key={item.id}
            component="button"
            type="button"
            role="tab"
            id={tabId(index)}
            ref={(node: HTMLButtonElement | null) => {
              tabRefs.current[index] = node;
            }}
            aria-selected={index === current}
            aria-controls={panelId(index)}
            tabIndex={index === current ? 0 : -1}
            onClick={() => setActive(index)}
            onKeyDown={onKeyDown}
            className="bf-ui"
            sx={[
              actionSx(index === current ? "ink" : "outline", "small"),
              {
                cursor: "pointer",
                flex: "none",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              },
            ]}
          >
            {item.label}
          </Box>
        ))}
      </Box>

      {items.map((item, index) => (
        <Box
          key={item.id}
          role="tabpanel"
          id={panelId(index)}
          aria-labelledby={tabId(index)}
          hidden={index !== current}
          sx={{ pt: 0.875 }}
        >
          {item.content}
        </Box>
      ))}
    </Box>
  );
}
